/**
 * ScheduleTrigger — the FIRST node in the routine graph (v2 §0 keystone).
 *
 * The schedule is a first-class node (not a header chip): it owns the two trigger properties —
 * `whenToRun` (the cron/cadence, edited via an INLINE horizontal picker) and `start` (a prominent
 * Active toggle that activates/pauses the routine). Both are settable by the human (when the agent is
 * idle) and by the agent. Replaces the old header Start/Pause buttons + the popup schedule chip.
 */
import { Clock } from 'lucide-react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/_shared/components/ui/select';
import { Switch } from '@/_shared/components/ui/switch';

import { CADENCES } from '../constants';
import { schedulePhrase } from '../lib/schedule-label';
import type { Cadence, Schedule } from '../types';
import { Connector } from './Connector';
import { TimezoneCombobox } from './TimezoneCombobox';

/** Cadences with no time-of-day component — the time input is hidden. */
const TIMELESS_CADENCES: ReadonlySet<Cadence> = new Set<Cadence>(['manual', 'hourly']);

/**
 * Cadences where timezone is irrelevant (run-on-demand has no wall-clock anchor).
 * Hourly KEEPS timezone — "top of the hour" is timezone-relative.
 */
const TIMEZONE_IRRELEVANT_CADENCES: ReadonlySet<Cadence> = new Set<Cadence>(['manual']);

interface ScheduleTriggerProps {
  schedule: Schedule;
  onScheduleChange: (next: Schedule) => void;
  /** `start` — whether the routine is live (running on its schedule). */
  isActive: boolean;
  /** Whether the routine has been saved at least once (drives the "Not saved" vs Active/Inactive badge). */
  isSaved: boolean;
  /** Toggle live/paused (start ⇄ pause). */
  onToggleActive: (active: boolean) => void;
  isToggling: boolean;
  /** A routine must be saved before it can go live (no workflow id yet ⇒ toggle disabled). */
  canToggle: boolean;
  /** Read-only while the agent is mid-turn. */
  readOnly: boolean;
  /**
   * ACT2-1286: the routine is paused because its warehouse account was removed. The switch then reads
   * Resume, and only an admin may flip it, after every step is repaired (`resumeBlockedReason`).
   */
  repairPaused?: boolean;
  /** Why Resume is unavailable while repair-paused; null when it may be flipped. */
  resumeBlockedReason?: string | null;
  /** Name the switch Resume (admins). A member never sees Resume (spec UI 15); theirs stays Active. */
  resumeLabel?: boolean;
}

export function ScheduleTrigger({
  schedule,
  onScheduleChange,
  isActive,
  isSaved,
  onToggleActive,
  isToggling,
  canToggle,
  readOnly,
  repairPaused = false,
  resumeBlockedReason = null,
  resumeLabel = true,
}: ScheduleTriggerProps): React.ReactElement {
  const showTime = !TIMELESS_CADENCES.has(schedule.cadence);
  const showTimezone = !TIMEZONE_IRRELEVANT_CADENCES.has(schedule.cadence);

  const switchEl = (
    <Switch
      checked={isActive}
      onCheckedChange={onToggleActive}
      loading={isToggling}
      disabled={!canToggle || resumeBlockedReason !== null}
      size="lg"
      aria-label={repairPaused && resumeLabel ? 'Resume' : 'Active'}
    />
  );
  const toggleReason = !canToggle ? 'Save the routine before activating it' : resumeBlockedReason;

  return (
    <>
      <div className="rounded-2xl border border-border bg-card shadow-base">
        <header className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="flex size-7 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Clock className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[0.6875rem] font-medium tracking-wider text-muted-foreground uppercase">
              <span>Trigger</span>
              <span className="size-1 rounded-full bg-border" />
              <span>Schedule</span>
            </div>
            <h3 className="truncate text-sm font-semibold text-foreground">When to run</h3>
          </div>

          {/* Active toggle — the routine's on/off (start ⇄ pause). The lifecycle badge (Active/Inactive)
              only applies once saved; a never-saved routine has no lifecycle yet — its "Draft" state is
              shown by the header chip, and the disabled toggle + tooltip say why it can't go live. */}
          <div className="flex items-center gap-2.5">
            {isSaved && (
              <Badge variant={isActive ? 'success' : repairPaused ? 'warning' : 'info'} bordered>
                {isActive ? 'Active' : repairPaused ? 'Paused for repair' : 'Inactive'}
              </Badge>
            )}
            {/* The disabled switch cannot take focus, so ActionTooltip's wrapper is the tab stop that
                lets a keyboard user read why. */}
            <ActionTooltip show={toggleReason !== null} message={toggleReason ?? ''}>
              {switchEl}
            </ActionTooltip>
          </div>
        </header>

        <div className="space-y-2.5 px-4 py-3">
          {/* Inline, flat-horizontal picker (no popup). Fields wrap gracefully on narrow widths, and go
              DISABLED (not swapped for static text) while the agent owns the draft, so the field layout
              reads the same across states. */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-36 flex-col gap-1">
              <Label htmlFor="schedule-cadence" className="text-xs font-medium text-muted-foreground">
                Cadence
              </Label>
              <Select
                value={schedule.cadence}
                onValueChange={(value) => onScheduleChange({ ...schedule, cadence: value as Cadence })}
                disabled={readOnly}
              >
                <SelectTrigger id="schedule-cadence" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CADENCES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {showTime && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="schedule-time" className="text-xs font-medium text-muted-foreground">
                  Time
                </Label>
                <Input
                  id="schedule-time"
                  type="time"
                  value={schedule.time}
                  onChange={(e) => onScheduleChange({ ...schedule, time: e.target.value })}
                  className="w-32"
                  disabled={readOnly}
                />
              </div>
            )}

            {showTimezone && (
              <div className="flex min-w-44 flex-col gap-1">
                <Label htmlFor="schedule-timezone" className="text-xs font-medium text-muted-foreground">
                  Timezone
                </Label>
                <TimezoneCombobox
                  id="schedule-timezone"
                  value={schedule.timezone}
                  onChange={(timezone) => onScheduleChange({ ...schedule, timezone })}
                  disabled={readOnly}
                />
              </div>
            )}
          </div>

          {/* Live human-readable summary — gives immediate feedback on the resulting schedule. */}
          <p className="text-[0.8125rem] text-muted-foreground">Runs {schedulePhrase(schedule)}</p>
        </div>
      </div>

      <Connector />
    </>
  );
}
